import React, { useEffect, useState } from 'react';
import { useDisclosure } from '@mantine/hooks';
import { ScrollArea } from '@mantine/core';
import { ScrollText, Database } from 'lucide-react';

import { EButton, EModal } from 'libs/ui';
import { groupByKey } from 'utils/utility';
import { IBookingExternalAPILog } from 'libs/data-access/types';
import { useExternalAPILogData, useMediaData } from 'libs/data-access';

import { ProviderTabs } from './ProviderTabs';
import { LoadingState } from './LoadingState';
import { EmptyState } from './EmptyState';
import { downloadFile, createAndDownloadZip } from './utils';

interface IProps {
    traceId: string;
}

export default function ExternalAPILog({ traceId }: IProps) {

    const { getExternalAPILogData } = useExternalAPILogData();
    const { getMediaData } = useMediaData();

    const [isOpen, { open, close }] = useDisclosure(false);
    const [externalAPILog, setExternalAPILog] = useState<{ [supplier: string]: IBookingExternalAPILog[] }>(
        {} as { [supplier: string]: IBookingExternalAPILog[] }
    );
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isDownloading, setIsDownloading] = useState<boolean>(false);
    const [isBulkDownloading, setIsBulkDownloading] = useState<boolean>(false);
    const [downloadProgress, setDownloadProgress] = useState<{ current: number; total: number } | null>(null);


    useEffect(() => {
        isOpen && getExternalAPILogByTraceId(traceId);
    }, [isOpen]);


    // API CALLS
    const getExternalAPILogByTraceId = async (traceId: string) => {
        setIsLoading(true);

        const response = await getExternalAPILogData(`/by-trace-id?traceId=${traceId}`);

        if (response.isSuccess) {
            const groupedBySupplier = groupByKey(response.data?.data, "apiProvider");
            setExternalAPILog(groupedBySupplier);
        } else {
            console.log("Error while getting external API log by traceId: ", response);
        };

        setIsLoading(false);
    };

    const handleDownload = async (mediaId: number, fileName: string) => {
        setIsDownloading(true);

        const response = await getMediaData(`?id=${mediaId}`);

        if (response.isSuccess) {
            downloadFile(response.data, fileName);
        } else {
            console.log("Error while getting media data by id: ", response);
        };

        setIsDownloading(false);
    };

    const handleBulkDownload = async (providerName: string, logs: IBookingExternalAPILog[]) => {
        setIsBulkDownloading(true);
        setDownloadProgress({ current: 0, total: 0 });

        try {
            await createAndDownloadZip(
                logs,
                providerName,
                traceId,
                async (mediaId: number) => {
                    return await getMediaData(`?id=${mediaId}`);
                },
                (current: number, total: number) => {
                    setDownloadProgress({ current, total });
                }
            );
        } catch (error) {
            console.error("Error during bulk download:", error);
        } finally {
            setIsBulkDownloading(false);
            setDownloadProgress(null);
        }
    };

    const hasLogs = Object.keys(externalAPILog || {}).length > 0;

    return (
        <>
            <EButton
                size="sm"
                variant="light"
                color="blue"
                onClick={open}
                leftSection={<ScrollText size={16} />}
                className="hover:bg-blue-50"
            >
                External Logs
            </EButton>

            <EModal
                size="70%"
                opened={isOpen}
                onClose={close}
                title={
                    <div className="flex items-center gap-2">
                        <Database size={20} />
                        <span className="font-semibold text-lg">External API Log</span>
                    </div>
                }
            >
                <ScrollArea h={600} type="auto" className="px-1">
                    {isLoading ? (
                        <LoadingState />
                    ) : !hasLogs ? (
                        <EmptyState />
                    ) : (
                        <ProviderTabs
                            externalAPILog={externalAPILog}
                            traceId={traceId}
                            isDownloading={isDownloading}
                            isBulkDownloading={isBulkDownloading}
                            downloadProgress={downloadProgress}
                            onDownload={handleDownload}
                            onBulkDownload={handleBulkDownload}
                        />
                    )}
                </ScrollArea>
            </EModal>
        </>
    );
}